import {h, Fragment} from "preact"
import { SerpElement, useCurrencyFormatting } from "@nosto/preact"
import { useEffect, useMemo, useState } from "preact/hooks"
import { productImagePlaceholder, getSrcset } from "../helpers"
import { productBadges, colors } from '../config'

import Reviews from "./Reviews"
import AddToCart from "./AddToCart"
import { SplideCarousel, SplideSlide } from "./SplideCarousel"

export default function Product({ product, highPrioImage, groupedProducts }) {
    const { format } = useCurrencyFormatting()
    
    const [ activeSwatch, setActiveSwatch ] = useState(product);
    
    /* --- PROMO LOGIC COMMENTED OUT ---
    const country = window.Shopify?.country;
    const promoTagMap = {
        AU: 'WK3Promo-AU',
        NZ: 'WK3Promo-AU',
        US: 'WK3Promo-US',
    };
    var newBadgeText = "Flash Sale | 30% OFF"

    const promoTag = promoTagMap[country];
    console.log('country: ' + country);
    if(country == "US"){
        var newBadgeText = "Mid Summer Sale | 30% OFF"
    }

    const hasPromoTag = !!promoTag && product.tags1?.includes(promoTag);
    console.log('hasPromo' + hasPromoTag);
    ----------------------------------- */

    const hasSwatches = useMemo(() => product.tags1?.some(f => f.startsWith("colourway-")), [])
    const handle = useMemo(() => activeSwatch.url.split('/products/')[1], [ activeSwatch ]);
    const isDiscounted = useMemo(() => activeSwatch.listPrice && activeSwatch.listPrice > activeSwatch.price, [ activeSwatch ]);
    const discountPercent = useMemo(() => Math.round(((activeSwatch.listPrice - activeSwatch.price) / activeSwatch.listPrice) * 100), [ activeSwatch ]);
    
    // Check for the specific Pre-Order tag for the title tag
    const hasPreOrderTag = useMemo(() => activeSwatch.tags1?.includes('stickers:Pre-Order'), [activeSwatch]);

    // Unified badge logic with priority list and max 2 limit
    const badgesToDisplay = useMemo(() => {
        const list = [];

        // Priority 3: Sold Out
        if (activeSwatch.availability === "OutOfStock") {
            list.push({ id: 'sold-out', text: 'Sold Out', priority: 3 });
        }

        // Priority 1: Pre Order (checking config badges) 
        // Priority 5: Any other config badges
        const filteredBadges = productBadges.filter(b => activeSwatch.tags1?.includes(b.tag));
        filteredBadges.forEach(b => {
            const isPreOrder = b.text.toLowerCase() === 'pre order' || b.tag.toLowerCase() === 'pre-order' || b.tag.toLowerCase() === 'stickers:pre-order';
            list.push({ id: b.tag, text: b.text, priority: isPreOrder ? 1 : 5 });
        });

        // Priority 4: Discount (Sale)
        if (isDiscounted && discountPercent > 0) {
            list.push({ id: 'discount', text: `Save ${discountPercent}%`, priority: 4 });
        }

        // Priority 2: Tall Fit
        if (activeSwatch.tags1?.includes('tall-fit')) {
            list.push({ id: 'tall-fit', text: 'Tall Fit', priority: 2 });
        }

        // Deduplicate by text to avoid repeating badges
        const uniqueList = Array.from(new Map(list.map(b => [b.text, b])).values());

        // Sort by priority (lowest number first) and limit to top 2
        uniqueList.sort((a, b) => a.priority - b.priority);
        return uniqueList.slice(0, 2);
    }, [activeSwatch, isDiscounted, discountPercent]);

    return (
        <SerpElement
            as="div"
            hit={activeSwatch}
            ariaLabel={`Product ${activeSwatch.name}`}
            class="ns-product"
        >
            
            <div class="abel-here ns-media-wrap ns-relative ns-w-100">
                <div class="ns-badge-wrap ns-absolute ns-flex ns-flex-col" >
                    
                    {badgesToDisplay.map(badge => {
                        // Color styling logic for Dark Badges (Pre Order & Tall Fit)
                        const isPreOrder = badge.text.toLowerCase() === 'pre order';
                        const isTallFit = badge.id === 'tall-fit';
                        const isDarkBadge = isPreOrder || isTallFit;
                        
                        const color = isDarkBadge ? '#FFFFFF' : '#000000';
                        const bg = isDarkBadge ? '#000000' : '#FFFFFF';
                        const border = isDarkBadge ? '#000000' : '#FFFFFF';

                        return (
                            <div 
                                key={badge.id} 
                                class="ns-badge tag"
                                style={`color: ${color}; background: ${bg}; border: 1px solid ${border}; border-radius:30px;`}
                            >
                                {badge.text} 
                            </div>
                        );
                    })}

                </div>
                
                <AddToCart product={activeSwatch ?? product} />

                <SplideCarousel
                    className="ns-image-carousel"
                    ariaLabel={`${activeSwatch.name} image carousel`}
                    mount={activeSwatch.alternateImageUrls?.length > 0}
                    watchChildren
                    options={{
                        type: 'loop',
                        rewind: true,
                        // perPage: 1,
                        autoWidth: true,
                        perMove: 1,
                        arrows: true,
                        pagination: true,
                        drag: true,
                        snap: true,
                    }}
                >
                    <SplideSlide>
                        <div class="ns-img-wrap ns-relative ns-w-100">
                            <a href={activeSwatch.url}>
                                <img
                                    width={720}
                                    height={1117}
                                    loading={highPrioImage ? "eager" : "lazy"}
                                    fetchPriority={highPrioImage ? "high" : "low"}
                                    class="ns-img ns-absolute ns-block ns-w-100 ns-h-100"
                                    src={activeSwatch.imageUrl.replace('.jpg', '_720x.jpg').replace('.png', '_720x.png').replace('.webp', '_720x.webp')}
                                    srcset={getSrcset(activeSwatch.imageUrl, [320, 480, 640, 720, 1024, 1280])}
                                    alt={activeSwatch.name}
                                />
                            </a>
                        </div>
                    </SplideSlide>
                    {activeSwatch.alternateImageUrls?.length > 0 && (
                        <>
                        {activeSwatch.alternateImageUrls.slice(0, 2).map(image => (
                            <SplideSlide key={image}>
                                <div class="ns-img-wrap ns-relative ns-w-100">
                                    <a href={activeSwatch.url}>
                                        <img
                                            width={720}
                                            height={1117}
                                            loading="lazy"
                                            fetchPriority="low"
                                            class="ns-img ns-absolute ns-block ns-w-100 ns-h-100"
                                            src={image.replace('.jpg', '_720x.jpg').replace('.png', '_720x.png').replace('.webp', '_720x.webp')}
                                            srcset={getSrcset(image, [320, 480, 640, 720, 1024, 1280])}
                                            alt={activeSwatch.name}
                                        />
                                    </a>
                                </div>
                            </SplideSlide>
                        ))}
                        </>
                    )}
                </SplideCarousel>
            </div>

            <div class="ns-details ns-relative">
                {/* brand + wishlist */}
                <div class="ns-flex ns-flex-nowrap ns-items-center">
                    {activeSwatch.brand && 
                        <div class="ns-brand">{activeSwatch.brand}</div>
                    }
                    <wishlist-button-collection
                        data-variant-id={activeSwatch.skus?.[0]?.id}
                        data-product-handle={handle}
                        show-icon=""
                        icon="wishlist"
                        align-self="center"
                        align-content="center"
                        floating={'{ "position": {"placement": "top-end", "inset": true} }'}
                        class="ns-wishlist-btn ns-absolute"
                    >
                        <wk-button class="wk-floating" />
                    </wishlist-button-collection>
                </div>

                {/* reviews */}
                <Reviews 
                    productId={activeSwatch.productId}
                    value={activeSwatch.ratingValue} 
                />

                {/* name */}
                <a href={activeSwatch.url}>
                    <div class="ns-product-title">
                        {activeSwatch.name}
                    </div>
                </a>

                

                <div class="ns-product-footer ns-flex ns-flex-col ns-lg-flex-row ns-lg-justify-between ns-lg-flex-wrap">
                    {/* price */}
                    
                    <div className="ns-price-wrap ns-flex ns-items-center">
                    {(isDiscounted) && (
                    <>
                        <span className="sr-only visually-hidden">Original price:</span>
                        <span className="ns-list-price">{format(activeSwatch.listPrice)}</span>
                    </>
                    )}
                    <span className="sr-only visually-hidden">Regular price:</span>
                    <span className="ns-price">{format(activeSwatch.price)}</span>
                </div>

               

                    {/* swatches CAROUSEL */}
                    {(groupedProducts?.products) ? (
                        <div class={`ns-swatches-container ${groupedProducts.products.length > 4 ? 'ns-scroll' : ''}`}>
                            <SplideCarousel
                                ariaLabel={`${activeSwatch.name} swatch`}
                                mount={groupedProducts.products.length > 4}
                                options={{
                                    type: 'slide',
                                    rewind: true,
                                    arrows: groupedProducts.products.length > 4,
                                    pagination: false,
                                    drag: true,
                                    snap: true,
                                    mediaQuery: 'min',
                                    perPage: 4,
                                    gap: 6.25,
                                    breakpoints: {
                                        990: { 
                                            perPage: 4,
                                            gap: 1
                                        }
                                    }
                                }}
                            >
                                {[...groupedProducts.products].sort((a, b) => {
                                    if (a.productId === product.productId) return -1;
                                    if (b.productId === product.productId) return 1;
                                    return 0;
                                }).map(swatchProduct => (
                                    <SplideSlide key={product.productId + swatchProduct.productId}>
                                        <button
                                            class="ns-swatch-wrap ns-flex ns-items-center ns-justify-center"
                                            type="button"
                                            aria-pressed={activeSwatch.productId === swatchProduct.productId}
                                            aria-label={`Select color for ${product.name}`}
                                            onClick={(e) => { 
                                                e.preventDefault();
                                                e.stopImmediatePropagation();
                                                setActiveSwatch(swatchProduct);
                                            }}  
                                        >
                                            <span 
                                                class="ns-swatch" 
                                                role="presentation" 
                                                aria-hidden="true" 
                                                style={`background: ${colors[swatchProduct.color?.toLowerCase()?.trim()] ?? "#e0e0e0"}`}
                                            />
                                            <span class="visually-hidden sr-only">Color {swatchProduct.color}</span>
                                        </button>                                      
                                    </SplideSlide>
                                ))}
                            </SplideCarousel>
                        </div>
                    ) : hasSwatches ? (
                        <div class="ns-swatch-skeleton" />
                    ) : null}
                </div>
                {/* NEW PRE-ORDER TITLE TAG */}
                 {hasPreOrderTag && (
                    <div style="background-color:#222222; color:#FFFFFF; display: inline-block; padding: 2px 8px; margin-top: 4px; font-size: 13px; font-weight: 400; letter-spacing: 0; line-height: 1.3em;" class="ns-preorder-text">
                        Pre Order
                    </div>
                )}
            </div>
        </SerpElement>
    )
}